import * as XLSX from "xlsx";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "../../Constants/Strings";

const DEFAULT_PAGE_SIZE = 1000;

export const DownloadExcelBill = async ({
    fetchFn,
    fieldMappings,
    fileName = "data.xlsx",
    pageSize = DEFAULT_PAGE_SIZE,
}) => {
    let allData = [];
    let offset = 0;
    let totalCount = null;

    try {
        // Step 1: Fetch paginated data
        while (true) {
            const response = await fetchFn({ limit: pageSize, offset });
            const results = response?.data?.results || [];
            const count = response?.data?.count || 0;

            if (totalCount === null) totalCount = count;
            if (results.length === 0) break;

            allData = allData.concat(results);
            offset += pageSize;
            if (allData.length >= totalCount) break;
        }
        // Step 2: Format data
        const formatted = allData.map((item) => {
            const row = {};
            fieldMappings.forEach(({ label, value }) => {
                
                const fieldValue = typeof value === "function" ? value(item) : item?.[value];
                row[label] = fieldValue != null && fieldValue !== "" ? fieldValue : "N/A";
            });
            return row;
        });
        // Step 3: Create Excel workbook and convert to Blob
        const worksheet = XLSX.utils.json_to_sheet(formatted);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

        const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Step 4: Handle download for Web vs Mobile
        await saveExcelBlob(blob, fileName);
    } catch (error) {
        toast.error(TOAST_MESSAGES.EXCEL_DOWNLOAD_FAILED || "Failed to download Excel.");
    }
};

// Saves an Excel blob: browser download on web, Documents folder on mobile
export const saveExcelBlob = async (blob, fileName) => {
    const isMobile = Capacitor.getPlatform() !== "web";
    const filePath = `Downloads/${fileName}`;

    if (isMobile) {
        const base64Data = await blobToBase64(blob);
        try {
          await Filesystem.writeFile({
            path: filePath,
            data: base64Data,
            directory: Directory.Documents,
            recursive: true,
          });
        } catch (e) {
          await Filesystem.writeFile({
            path: filePath,
            data: base64Data,
            directory: Directory.ExternalStorage,
            recursive: true,
          });
        }

        toast.success(TOAST_MESSAGES.EXCEL_MOBILE_DOWNLOAD_SUCCESS || "Excel file saved to device.");
    } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(TOAST_MESSAGES.EXCEL_DOWNLOAD_SUCCESS || "Excel downloaded successfully.");
    }
};

// Helper function to convert Blob to base64
const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const base64 = reader.result.split(",")[1]; // remove data prefix
            resolve(base64);
        };
        reader.readAsDataURL(blob);
    });
